'use client';

import * as React from 'react';

import { cn } from '@lib';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { IconX } from '@tabler/icons-react';
import type { SocialWithContact } from '@types';
import { type VariantProps, cva } from 'class-variance-authority';

import { Button } from '../components/button';
import { SocialIcons } from '../components/social-icons';

export const classNames = {
  overlay:
    'fixed inset-0 z-50 bg-black/80 backdrop-blur-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
};

export const sheetVariants = cva(
  'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 border-white/10 p-4 py-5 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
  {
    variants: {
      side: {
        top: 'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 border-b',
        bottom:
          'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 border-t',
        left: 'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-full max-w-sm border-r',
        right:
          'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-full max-w-sm border-l',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  },
);

interface SheetProps extends React.ComponentProps<typeof SheetRoot> {
  title: string;
  description?: string;
  socials?: SocialWithContact;
  hideTitle?: boolean;
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

function Sheet({ children, title, description, socials, hideTitle = false, side, className, ...props }: SheetProps) {
  return (
    <SheetRoot {...props}>
      <SheetContent className={className} side={side}>
        {(!hideTitle || description) && (
          <SheetHeader>
            {!hideTitle && <SheetTitle>{title}</SheetTitle>}
            {description && <SheetDescription>Sheet description</SheetDescription>}
          </SheetHeader>
        )}
        {children}
        {socials && (
          <SheetFooter>
            <SocialIcons className="justify-center" links={socials} />
          </SheetFooter>
        )}
      </SheetContent>
    </SheetRoot>
  );
}

const SheetRoot = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ComponentRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay className={cn(classNames.overlay, className)} {...props} ref={ref} />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

const SheetContent = React.forwardRef<React.ComponentRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  ({ side = 'right', children, className, ...props }, ref) => (
    <>
      <SheetOverlay />
      <SheetPrimitive.Content ref={ref} {...props} className={cn(sheetVariants({ side }), className)}>
        {children}
        <SheetPrimitive.Close
          asChild
          className="absolute top-4 right-4 opacity-70 transition-opacity hover:opacity-100"
        >
          <Button size={'smIcon'} variant={'ghost'}>
            <IconX size={20} />
            <span className="sr-only">Close</span>
          </Button>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </>
  ),
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-2 text-left', className)} {...props} />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex w-full items-center justify-center', className)} {...props} />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ComponentRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={cn('font-heading text-foreground text-lg', className)} {...props} />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ComponentRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description ref={ref} className={cn('text-muted-foreground text-sm', className)} {...props} />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};

export default Sheet;
